import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionMeta,
  CreateLeadInput,
  Lead,
  LeadCounts,
  LeadListQuery,
  UpdateLeadInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['leads'] as const;

export interface LeadPage {
  items: Lead[];
  meta: CollectionMeta;
}

function toQuery(q: LeadListQuery): Record<string, string | number | boolean | undefined> {
  return {
    page: q.page,
    pageSize: q.pageSize,
    search: q.search || undefined,
    pipelineId: q.pipelineId,
    stageId: q.stageId,
    status: q.status,
    assignedUserId: q.assignedUserId,
    contactId: q.contactId,
    tagId: q.tagId,
    contact: q.contact || undefined,
    valueMin: q.valueMin,
    valueMax: q.valueMax,
    createdFrom: q.createdFrom,
    createdTo: q.createdTo,
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
  };
}

export async function listLeads(q: LeadListQuery): Promise<LeadPage> {
  const res = await apiClient.getCollection<Lead>('/leads', { query: toQuery(q) });
  return { items: res.data, meta: res.meta };
}
export const getLead = (id: string) => apiClient.get<Lead>(`/leads/${id}`);
export const createLead = (input: CreateLeadInput) => apiClient.post<Lead>('/leads', input);
export const updateLead = (id: string, patch: UpdateLeadInput) =>
  apiClient.patch<Lead>(`/leads/${id}`, patch);
export const deleteLead = (id: string) => apiClient.delete<void>(`/leads/${id}`);

export function useLeads(query: LeadListQuery, enabled = true) {
  return useQuery<LeadPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listLeads(query),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** All / per-status counts for the grid tabs, under the same filters (status ignored). */
export function useLeadCounts(query: LeadListQuery, enabled = true) {
  const { page: _p, pageSize: _ps, sortBy: _s, sortOrder: _o, status: _st, ...filters } = query;
  return useQuery<LeadCounts, ApiClientError>({
    queryKey: [...KEY, 'counts', filters],
    queryFn: () => apiClient.get<LeadCounts>('/leads/counts', { query: toQuery(filters) }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useLead(id: string | undefined) {
  return useQuery<Lead, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getLead(id!),
    enabled: Boolean(id),
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation<Lead, ApiClientError, CreateLeadInput>({
    mutationFn: createLead,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateLead(id: string) {
  const qc = useQueryClient();
  return useMutation<Lead, ApiClientError, UpdateLeadInput>({
    mutationFn: (patch) => updateLead(id, patch),
    onSuccess: (lead) => {
      qc.setQueryData([...KEY, 'detail', id], lead);
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/**
 * Same PATCH as `useUpdateLead`, but takes the lead id per-call instead of
 * fixing it at hook-creation time - for the Kanban board's drag-and-drop,
 * where the dragged lead's id is only known at drop time, not when the
 * drop-target column itself renders.
 */
export function useMoveLead() {
  const qc = useQueryClient();
  return useMutation<Lead, ApiClientError, { id: string; stageId: string }>({
    mutationFn: ({ id, stageId }) => updateLead(id, { stageId }),
    onSuccess: (lead) => {
      qc.setQueryData([...KEY, 'detail', lead.id], lead);
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteLead,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
